import { z } from "zod";
import apiClient from "../../../shared/api/client";

// `/admin/...` y no `/me/...` a propósito.
//
// `AccessKeyGuard` elige la credencial por el prefijo de la URL: en una ruta
// que no empieza con `/admin` prefiere la cookie de usuario. Como el dueño del
// backoffice también está logueado como usuario en app.eriol.io y las dos
// cookies viajan juntas, la suscripción quedaba guardada como del USUARIO.
//
// El síntoma era engañoso: la prueba manual llegaba —se manda al dueño de la
// suscripción— pero los avisos operativos, que van a los admins, no llegaban
// nunca porque no existía ninguna suscripción de admin.
const BASE = "/admin/push-subscriptions";

const publicKeyResponseSchema = z.object({
  publicKey: z.string().nullable(),
  enabled: z.boolean(),
});

const subscriptionRowSchema = z.object({
  id: z.string(),
  userAgent: z.string().nullable(),
  createdAt: z.string(),
  lastUsedAt: z.string(),
});

const subscribeResponseSchema = z.object({
  id: z.string(),
});

const unsubscribeResponseSchema = z.object({
  deleted: z.boolean(),
});

const testResponseSchema = z.object({
  delivered: z.number(),
  failed: z.number(),
  expired: z.number(),
});

export type PushSubscriptionRow = z.infer<typeof subscriptionRowSchema>;

export interface SubscribePushPayload {
  endpoint: string;
  p256dh: string;
  auth: string;
}

export const fetchPushPublicKey = async () => {
  const { data } = await apiClient.get(`${BASE}/public-key`);
  return publicKeyResponseSchema.parse(data);
};

export const fetchPushSubscriptions = async (): Promise<
  PushSubscriptionRow[]
> => {
  const { data } = await apiClient.get(BASE);
  return z.array(subscriptionRowSchema).parse(data);
};

export const subscribePush = async (
  payload: SubscribePushPayload,
): Promise<{ id: string }> => {
  const { data } = await apiClient.post(BASE, payload);
  return subscribeResponseSchema.parse(data);
};

export const unsubscribePush = async (id: string): Promise<void> => {
  await apiClient.delete(`${BASE}/${id}`);
};

/**
 * Baja de la suscripción de ESTE navegador, por endpoint.
 *
 * Durante la migración, la suscripción que el navegador tiene registrada
 * todavía puede ser del USUARIO: no aparece en la lista del admin, así que no
 * hay id que mandar. Buscarla por el id de esa lista borraba OTRO dispositivo.
 *
 * Van también las claves: identifican ESA suscripción. Si otra pestaña volvió a
 * suscribirse mientras tanto, las claves son otras y no se borra nada.
 */
export const unsubscribePushByEndpoint = async (
  payload: SubscribePushPayload,
): Promise<{ deleted: boolean }> => {
  const { data } = await apiClient.post(`${BASE}/unsubscribe`, payload);
  return unsubscribeResponseSchema.parse(data);
};

export const sendTestPush = async () => {
  const { data } = await apiClient.post(`${BASE}/test`, {});
  return testResponseSchema.parse(data);
};
